import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Link, TextField, Typography } from '@material-ui/core';
import { GfmMarkdownContent } from '../catalog/GfmMarkdownContent';
import { useAskAiStyles } from './askAiStyles';
import type { CodebaseScope } from './api';
import type {
  AskFailure,
  AskTurn,
  CodebaseConversation,
  PendingAsk,
} from './useCodebaseConversation';

const REPOS_NAMED = 3;

function formatTime(iso?: string): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Plain sentences for each way a question can end without an answer. */
export function failureMessage(failure: AskFailure): string {
  switch (failure.kind) {
    case 'denied':
      return failure.message
        ? `You don't have access to ask that. ${failure.message}`
        : "You don't have access to ask that.";
    case 'rateLimited': {
      const time = formatTime(failure.resetAt);
      return time
        ? `You've reached your hourly question limit. It resets at ${time}, so try again then.`
        : "You've reached your hourly question limit. Try again later.";
    }
    case 'signIn':
      return "Ask AI couldn't confirm your Microsoft sign-in. Reload the page and try again.";
    case 'tooLong':
      return 'That question is too long to send. Shorten it and try again.';
    case 'stopped':
      return 'You stopped waiting for this answer. The question still counts toward your hourly limit.';
    default:
      return "Ask AI couldn't get an answer right now. Try again in a few minutes.";
  }
}

function RepoNames({ repos }: { repos: CodebaseScope['repos'] }) {
  const [expanded, setExpanded] = useState(false);
  const names = repos.map(({ repo }) => repo);
  const rest = names.length - REPOS_NAMED;
  if (expanded || rest <= 0) return <>{names.join(', ')}</>;
  return (
    <>
      {names.slice(0, REPOS_NAMED).join(', ')} and{' '}
      <Link component="button" onClick={() => setExpanded(true)}>
        {rest} more
      </Link>
    </>
  );
}

function Turn({ turn }: { turn: AskTurn }) {
  const classes = useAskAiStyles();
  const { answer, failure } = turn;
  return (
    <li className={classes.turn}>
      <p className={classes.question}>{turn.question}</p>
      {answer && (
        <>
          {/* Images stay links: an image URL in an answer steered by planted
              text in the code could carry data out the moment it loads. */}
          <GfmMarkdownContent
            content={answer.answer}
            className={classes.answer}
            linkImages
          />
          {answer.truncated && (
            <p className={classes.meta}>This answer was cut short.</p>
          )}
          {answer.branchesConsulted.length > 0 && (
            <p className={classes.meta}>
              Branches consulted: {answer.branchesConsulted.join(', ')}
            </p>
          )}
          {answer.filesConsulted.length > 0 && (
            <details className={classes.meta}>
              <summary>
                Files consulted ({answer.filesConsulted.length})
              </summary>
              <ul className={classes.files}>
                {answer.filesConsulted.map(file => (
                  <li key={file}>{file}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      {failure && <p className={classes.status}>{failureMessage(failure)}</p>}
    </li>
  );
}

function elapsedLabel(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function Working({
  pending,
  onStop,
}: {
  pending: PendingAsk;
  onStop: () => void;
}) {
  const classes = useAskAiStyles();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <li className={classes.turn}>
      <p className={classes.question}>{pending.question}</p>
      <div className={classes.status}>
        <strong>Working on your answer</strong>{' '}
        <span role="timer" aria-label="Elapsed time">
          {elapsedLabel(now - pending.startedAt)}
        </span>
        <p>Answers can take up to about four minutes.</p>
        <Button variant="outlined" onClick={onStop}>
          Stop waiting
        </Button>
      </div>
    </li>
  );
}

/** Announces the start and end of each question to assistive technology. */
function liveMessage(conversation: CodebaseConversation): string {
  if (conversation.pending) return 'Working on your answer.';
  const last = conversation.turns[conversation.turns.length - 1];
  if (!last) return '';
  return last.answer ? 'Answer ready.' : failureMessage(last.failure!);
}

/** The drawer for members of a codebase group: ask, wait, read, follow up. */
export function AskAiCodebase({
  repos,
  conversation,
}: {
  repos: CodebaseScope['repos'];
  conversation: CodebaseConversation;
}) {
  const classes = useAskAiStyles();
  const [question, setQuestion] = useState('');
  const questionField = useRef<HTMLTextAreaElement>(null);
  const { turns, pending } = conversation;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!question.trim() || pending) return;
    conversation.ask(question.trim());
    setQuestion('');
    // Ask disables itself while waiting; keep focus in the drawer, ready for
    // the follow-up, rather than letting it fall to the page.
    questionField.current?.focus();
  };
  return (
    <>
      <Typography component="h3" className={classes.title}>
        Ask about your code
      </Typography>
      <p className={classes.description} id="ask-ai-description">
        Answers come from the code in <RepoNames repos={repos} />.
      </p>
      {(turns.length > 0 || pending) && (
        <ol className={classes.conversation} aria-label="Conversation">
          {turns.map(turn => (
            <Turn key={turn.id} turn={turn} />
          ))}
          {pending && (
            <Working pending={pending} onStop={conversation.stopWaiting} />
          )}
        </ol>
      )}
      <div aria-live="polite" className={classes.visuallyHidden}>
        {liveMessage(conversation)}
      </div>
      <form className={classes.form} onSubmit={submit}>
        <TextField
          id="ask-ai-question"
          fullWidth
          multiline
          minRows={3}
          inputRef={questionField}
          variant="outlined"
          label={turns.length > 0 ? 'Ask a follow-up' : 'Your question'}
          value={question}
          onChange={event => setQuestion(event.target.value)}
          onKeyDown={event => {
            // Not while an input method is composing: Enter confirms the
            // composed text there, it does not send.
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              submit(event);
            }
          }}
        />
        <div className={classes.actions}>
          <Button
            type="submit"
            disabled={!question.trim() || !!pending}
            color="primary"
            variant="contained"
          >
            Ask
          </Button>
          {turns.length > 0 && (
            <Button disabled={!!pending} onClick={conversation.newConversation}>
              New conversation
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
